import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';

import type { ChartViewport } from './chart-options';
import { applyZoomRange, readZoomRange, readZoomRangeFromEvent, FULL_ZOOM_RANGE, type ZoomRange } from './chart-zoom';
import { useContainerSize } from './use-container-size';

/**
 * Steps the visible share is rounded to before it can rebuild the option.
 * Dragging the slider fires continuously; the axis only has three states to
 * choose between, so 5% is far finer than it needs to be to look immediate.
 */
const VISIBLE_SHARE_STEP = 0.05;

export const quantiseShare = (share: number): number =>
  Math.min(1, Math.max(VISIBLE_SHARE_STEP, Math.round(share / VISIBLE_SHARE_STEP) * VISIBLE_SHARE_STEP));

/** Cannot occur inside a category name, so no two lists can share a signature. */
const CATEGORY_SEPARATOR = '\u0000';

/**
 * What the chart is plotting, as a value that changes exactly when the
 * categories do — which is when a zoom window stops describing anything.
 */
export const categorySignature = (option: EChartsOption): string => {
  const axes = option as { xAxis?: unknown; yAxis?: unknown };
  const [axis] = [axes.xAxis, axes.yAxis]
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((value): value is { data?: unknown[] } => Boolean((value as { data?: unknown[] } | undefined)?.data));

  return (axis?.data ?? []).join(CATEGORY_SEPARATOR);
};

/**
 * Which series the chart is drawing, by name. Together with the categories it
 * tells a new data set from a rebuild of the same one.
 */
export const seriesSignature = (option: EChartsOption): string => {
  const { series } = option as { series?: unknown };

  return (Array.isArray(series) ? series : series ? [series] : [])
    .map((entry) => String((entry as { name?: unknown }).name ?? ''))
    .join(CATEGORY_SEPARATOR);
};

/**
 * `setOption` merges by default, and a merge keeps every series nothing in the
 * new option mapped onto — filtering the chart down to one vehicle category left
 * the others on the canvas with the previous query's data (355494). So a new data
 * set replaces the series outright. A rebuild of the same data — a zoom step, a
 * resize — merges: replacing there recreated every series and every symbol at
 * each step of a slider drag.
 */
const REPLACED_ON_NEW_DATA = ['series'];

type ChartOption = EChartsOption | ((viewport: ChartViewport) => EChartsOption);

/**
 * Resolves a chart option against the room the chart actually has: the size of
 * its container, and how much of the data the dataZoom window is showing.
 *
 * Both are things ECharts will not tell an option builder, and both change
 * without the data changing — a sibling card collapsing, a slider drag — so the
 * option is rebuilt from them rather than authored once.
 */
export const useChartViewport = (option: ChartOption) => {
  const chartRef = useRef<ReactECharts>(null);
  const [containerRef, size, isMeasured] = useContainerSize<HTMLDivElement>();
  // The live window is a ref, not state: it is carried back into every rebuild
  // so the user is not snapped out of their zoom, but on its own it is no reason
  // to rebuild anything.
  const zoomRange = useRef<ZoomRange>(FULL_ZOOM_RANGE);
  const [visibleShare, setVisibleShare] = useState(1);

  const resolvedOption = useMemo(() => {
    const built = typeof option === 'function' ? option({ ...size, visibleShare }) : option;

    return applyZoomRange(built, zoomRange.current);
  }, [option, size, visibleShare]);

  // Keyed on the categories rather than on the identity of the `option` prop: a
  // caller that rebuilds its builder every render would otherwise drop the user
  // out of their zoom on every render of its parent.
  const signature = useMemo(() => categorySignature(resolvedOption), [resolvedOption]);
  const dataSignature = `${signature}${CATEGORY_SEPARATOR}${CATEGORY_SEPARATOR}${seriesSignature(resolvedOption)}`;
  const drawnDataSignature = useRef<string | undefined>(undefined);
  const replaceMerge = drawnDataSignature.current === dataSignature ? undefined : REPLACED_ON_NEW_DATA;

  useEffect(() => {
    drawnDataSignature.current = dataSignature;
  }, [dataSignature]);

  useEffect(() => {
    zoomRange.current = FULL_ZOOM_RANGE;
    setVisibleShare(1);
  }, [signature]);

  const handleDataZoom = useCallback((params: unknown) => {
    const range =
      readZoomRangeFromEvent(params) ??
      readZoomRange(chartRef.current?.getEchartsInstance().getOption() as EChartsOption | undefined);

    if (!range) {
      return;
    }

    zoomRange.current = range;
    setVisibleShare(quantiseShare((range.end - range.start) / 100));
  }, []);

  const chartEvents = useMemo(() => ({ dataZoom: handleDataZoom }), [handleDataZoom]);

  return { containerRef, chartRef, resolvedOption, replaceMerge, chartEvents, isMeasured };
};
